import React, { useEffect, useState } from "react";
import Base from "../admin_components/Admin_Base";
import "../EmployeeSection/Addemployee.css";
import { Breadcrumbs } from "@mui/material";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { ADMIN_API_BASE_URL } from "../Auth/Api";
import Swal from "sweetalert2";
import authAxios from "../Auth/useAxios";

const AddEmployee = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    dateOfBirth: "",
    mobile: "",
    email: "",
    location: "",
    designation: "",
    address: "",
    username: "",
    password: "",
    documentName: "",
    documentFile: null,
    managerReferenceId: "",
  });

  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState([]);
  const [filteredManagers, setFilteredManagers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hierarchy = {
    GM: [],
    AGM: ["GM"],
    DO: ["GM", "AGM"],
    DM: ["GM", "AGM", "DO"],
    MO: ["GM", "AGM", "DO", "DM"],
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await authAxios.get(
          `${ADMIN_API_BASE_URL}/main/admin/list-employee`
        );
        if (Array.isArray(response.data)) {
          setEmployees(response.data);
        } else {
          console.error("Unexpected API response:", response.data);
          setEmployees([]);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === "documentFile") {
      setFormData((prevData) => ({
        ...prevData,
        documentFile: files ? files[0] : null,
        // Auto-populate document name from file if documentName is empty
        documentName: prevData.documentName || (files && files[0] ? files[0].name : "")
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  useEffect(() => {
    if (formData.designation) {
      const allowedManagers = hierarchy[formData.designation] || [];
      const filtered = employees.filter((emp) => 
        allowedManagers.includes(emp.designation)
      );
      setFilteredManagers(filtered);
      
      // Reset manager selection if current manager is not in filtered list
      if (formData.managerReferenceId && 
          !filtered.some(manager => manager.employeeReferenceId === formData.managerReferenceId)) {
        setFormData(prev => ({ ...prev, managerReferenceId: "" }));
      }
    } else {
      setFilteredManagers([]);
      setFormData(prev => ({ ...prev, managerReferenceId: "" }));
    }
  }, [formData.designation, employees]);

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    } else {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      const minAgeDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
      
      if (dob > minAgeDate) {
        newErrors.dateOfBirth = "Employee must be at least 18 years old";
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Mobile validation (assuming 10 digits)
    const mobileRegex = /^\d{10}$/;
    if (!formData.mobile) {
      newErrors.mobile = "Mobile number is required";
    } else if (!mobileRegex.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }

    if (!formData.location.trim()) {
      newErrors.location = "Location is required";
    }

    if (!formData.designation) {
      newErrors.designation = "Designation is required";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }

    // Document validation
    if (!formData.documentName.trim()) {
      newErrors.documentName = "Document name is required";
    }

    if (!formData.documentFile) {
      newErrors.documentFile = "Document file is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    setIsSubmitting(true);
    
    try {
      const data = new FormData();
      
      // Append all form fields as per backend DTO
      data.append("fullName", formData.fullName);
      data.append("dateOfBirth", formData.dateOfBirth);
      data.append("mobile", formData.mobile);
      data.append("email", formData.email);
      data.append("location", formData.location);
      data.append("designation", formData.designation);
      data.append("address", formData.address);
      data.append("username", formData.username);
      data.append("password", formData.password);
      data.append("documentName", formData.documentName);
      
      if (formData.managerReferenceId) {
        data.append("managerReferenceId", formData.managerReferenceId);
      }
      
      if (formData.documentFile) {
        data.append("documentFile", formData.documentFile);
      }

      const response = await authAxios.post(
        `${ADMIN_API_BASE_URL}/main/admin/add-employee`, 
        data, 
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        await Swal.fire({
          icon: "success",
          title: "Employee Added!",
          text: response.data || "The employee has been successfully added.",
          confirmButtonColor: "#3085d6",
        });

        // Reset form
        setFormData({
          fullName: "",
          dateOfBirth: "",
          mobile: "",
          email: "",
          location: "",
          designation: "",
          address: "",
          username: "",
          password: "",
          documentName: "",
          documentFile: null,
          managerReferenceId: "",
        });
        
        setErrors({});
      }
    } catch (error) {
      console.error("Error Adding Employee:", error.response?.data || error.message);
      
      let errorMessage = "Failed to add employee. Please try again.";
      
      if (error.response?.status === 409) {
        errorMessage = error.response.data;
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data || "Invalid data provided";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      Swal.fire({
        icon: "error",
        title: "Error!",
        text: errorMessage,
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Base>
      <div
        className="pt-3 mt-5 res_container"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          paddingRight: "20px",
        }}
      >
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className="admin_navigation"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/admin/dashboard"
            sx={{ color: "darkslategrey", fontWeight: "bold" }}
          >
            Dashboard
          </Link>
          <Link
            underline="none"
            color="inherit"
            href="/admin/admin_add_employee"
            sx={{ color: "darkslategrey", fontWeight: "bold" }}
          >
            Create Employee
          </Link>
        </Breadcrumbs>
      </div>

      <h2 className="text-center fw-bold main_heading">Create Employee</h2>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <hr style={{ width: "94%" }} />
      </div>

      <div className="container p-3 d-flex justify-content-center align-items-center">
        <form
          className="p-4 border rounded shadow-sm bg-light w-100"
          style={{ maxWidth: "900px" }}
          onSubmit={handleSubmit}
        >
          <div className="row mb-3">
            <label htmlFor="fullName" className="col-sm-3 col-form-label custom-label">
              Employee Full Name:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                id="fullName"
                placeholder="Enter employee full name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
              {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="dateOfBirth" className="col-sm-3 col-form-label custom-label">
              Date of Birth:
            </label>
            <div className="col-sm-7">
              <input
                type="date"
                className={`form-control ${errors.dateOfBirth ? 'is-invalid' : ''}`}
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
              />
              {errors.dateOfBirth && <div className="invalid-feedback">{errors.dateOfBirth}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="mobile" className="col-sm-3 col-form-label custom-label">
              Phone Number:
            </label>
            <div className="col-sm-7">
              <input
                type="tel"
                className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                id="mobile"
                placeholder="10-digit mobile number"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                pattern="[0-9]{10}"
                required
              />
              {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="email" className="col-sm-3 col-form-label custom-label">
              Email:
            </label>
            <div className="col-sm-7">
              <input
                type="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                id="email"
                placeholder="Enter email address"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="location" className="col-sm-3 col-form-label custom-label">
              Location:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                id="location"
                name="location"
                placeholder="Enter location"
                value={formData.location}
                onChange={handleChange}
                required
              />
              {errors.location && <div className="invalid-feedback">{errors.location}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="designation" className="col-sm-3 col-form-label custom-label">
              Designation:
            </label>
            <div className="col-sm-7">
              <select
                className={`form-control ${errors.designation ? 'is-invalid' : ''}`}
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                required
              >
                <option value="">Select Designation</option>
                <option value="GM">General Manager [GM]</option>
                <option value="AGM">Assistant General Manager [AGM]</option>
                <option value="DO">Development Officer [DO]</option>
                <option value="DM">Development Manager [DM]</option>
                <option value="MO">Marketing Officer [MO]</option>
              </select>
              {errors.designation && <div className="invalid-feedback">{errors.designation}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="address" className="col-sm-3 col-form-label custom-label">
              Address:
            </label>
            <div className="col-sm-7">
              <textarea
                className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                id="address"
                name="address"
                placeholder="Enter complete address"
                value={formData.address}
                onChange={handleChange}
                rows="3"
                required
              />
              {errors.address && <div className="invalid-feedback">{errors.address}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="username" className="col-sm-3 col-form-label custom-label">
              Username:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                id="username"
                name="username"
                placeholder="Enter username"
                value={formData.username}
                onChange={handleChange}
                required
              />
              {errors.username && <div className="invalid-feedback">{errors.username}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="password" className="col-sm-3 col-form-label custom-label">
              Password:
            </label>
            <div className="col-sm-7">
              <input
                type="password"
                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                id="password"
                name="password"
                placeholder="Enter password (min. 6 characters)"
                value={formData.password}
                onChange={handleChange}
                required
              />
              {errors.password && <div className="invalid-feedback">{errors.password}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="documentName" className="col-sm-3 col-form-label custom-label">
              Document Name:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className={`form-control ${errors.documentName ? 'is-invalid' : ''}`}
                id="documentName"
                name="documentName"
                placeholder="Enter document name (e.g., Aadhar Card, Resume)"
                value={formData.documentName}
                onChange={handleChange}
                required
              />
              {errors.documentName && <div className="invalid-feedback">{errors.documentName}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="documentFile" className="col-sm-3 col-form-label custom-label">
              Upload Document:
            </label>
            <div className="col-sm-7">
              <input
                type="file"
                className={`form-control ${errors.documentFile ? 'is-invalid' : ''}`}
                id="documentFile"
                name="documentFile"
                onChange={handleChange}
                required
              />
              {errors.documentFile && <div className="invalid-feedback">{errors.documentFile}</div>}
              {formData.documentFile && (
                <small className="text-muted">
                  Selected file: {formData.documentFile.name}
                </small>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <label htmlFor="managerReferenceId" className="col-sm-3 col-form-label custom-label">
              Manager:
            </label>
            <div className="col-sm-7">
              <select
                className="form-control"
                id="managerReferenceId"
                name="managerReferenceId"
                value={formData.managerReferenceId}
                onChange={handleChange}
                disabled={!formData.designation || filteredManagers.length === 0}
              >
                <option value="">Select Manager (Optional)</option>
                {filteredManagers.map((manager) => (
                  <option
                    key={manager.employeeReferenceId}
                    value={manager.employeeReferenceId}
                  >
                    {manager.fullName} ({manager.designation}) - {manager.employeeReferenceId}
                  </option>
                ))}
              </select>
              {formData.designation && filteredManagers.length === 0 && (
                <small className="text-muted">No managers available for this designation</small>
              )}
              <small className="text-muted">Optional field - select a manager for this employee</small>
            </div>
          </div>

          <div className="employee_btn">
            <button 
              type="submit" 
              className="custom-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding Employee..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </Base>
  );
};

export default AddEmployee;